import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Clock, Layers, Image as ImageIcon, AlertTriangle, Download, FileText, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/Shell";
import { api, downloadAuthed, openJobReport } from "@/lib/api";
import { fmtDuration, fmtDateTime } from "@/lib/format";

const SentStatus = ({ s }) => {
  if (s === "delivered") return <span data-testid="sent-status-delivered" className="text-emerald-600 inline-flex items-center gap-1 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Delivered</span>;
  if (s === "bounced") return <span className="text-red-600 inline-flex items-center gap-1 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> Bounced</span>;
  if (s === "complained") return <span className="text-amber-600">Complaint</span>;
  return <span className="text-muted-foreground">Sent</span>;
};

const Stat = ({ icon: Icon, label, value, testid, color }) => (
  <div data-testid={testid} className="bg-card border border-border rounded-2xl p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="mt-2 font-display text-3xl font-black tracking-tight">{value}</p>
      </div>
      <span className={`w-9 h-9 rounded-lg bg-secondary flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></span>
    </div>
  </div>
);

export const ReportsTab = () => {
  const [summary, setSummary] = useState(null);
  const [sent, setSent] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.reportsSummary().then(setSummary).catch(() => toast.error("Failed to load reports"));
    api.reportsSent().then(setSent).catch(() => {});
  }, []);

  const exportCsv = async () => {
    setBusy(true);
    try { await downloadAuthed("/reports/export.csv", "crossjobs-report.csv"); toast.success("CSV downloaded"); }
    catch (e) { toast.error(e.message || "Export failed"); }
    finally { setBusy(false); }
  };

  const openJobPdf = async (jobId) => {
    try { await openJobReport(jobId); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not open PDF"); }
  };

  if (!summary) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;
  const perJob = summary.per_job || [];

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Reports</h2>
          <p className="text-sm text-muted-foreground">Time and site-photo activity across all jobs.</p>
        </div>
        <Button data-testid="export-csv-btn" onClick={exportCsv} disabled={busy} variant="outline" className="rounded-full">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Clock} label="Total time" value={fmtDuration(summary.total_seconds)} testid="report-total-time" color="text-primary" />
        <Stat icon={Layers} label="Total shifts" value={summary.total_shifts ?? 0} testid="report-total-shifts" color="text-blue-500" />
        <Stat icon={ImageIcon} label="Completion photos" value={summary.completion_photos ?? 0} testid="report-completion-photos" color="text-emerald-500" />
        <Stat icon={AlertTriangle} label="Issue photos" value={summary.issue_photos ?? 0} testid="report-issue-photos" color="text-amber-500" />
      </div>

      <div className="mt-8">
        <h3 className="font-display text-lg font-semibold mb-3">Per-job breakdown</h3>
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead className="text-right">Time</TableHead>
                <TableHead className="text-right">Shifts</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Completion</TableHead>
                <TableHead className="text-right hidden sm:table-cell">Issues</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">PDF</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perJob.map((j) => (
                <TableRow key={j.job_id} data-testid={`report-row-${j.job_id}`}>
                  <TableCell className="font-medium">{j.name}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtDuration(j.total_seconds)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{j.shifts}</TableCell>
                  <TableCell className="text-right hidden sm:table-cell">{j.completion_photos}</TableCell>
                  <TableCell className="text-right hidden sm:table-cell">{j.issue_photos}</TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" data-testid={`report-pdf-${j.job_id}`} onClick={() => openJobPdf(j.job_id, j.name)} className="h-8 w-8 rounded-full text-primary hover:bg-primary/10">
                      <FileText className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {perJob.length === 0 && (
            <div className="border-t border-dashed border-border px-5 py-12 text-center text-sm text-muted-foreground" data-testid="reports-empty">
              No job activity to report yet.
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Reports sent to clients</h3>
        <div className="bg-card border border-border rounded-2xl overflow-x-auto">
          <Table className="min-w-[560px]">
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Emailed to</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sent.map((r) => (
                <TableRow key={r.id} data-testid={`report-sent-${r.id}`}>
                  <TableCell className="font-medium">{r.job_name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.recipient_type === "supervisor" ? "Supervisor copy" : (r.client_name || "—")}</TableCell>
                  <TableCell className="font-mono text-sm">{r.to_email}</TableCell>
                  <TableCell className="text-sm"><SentStatus s={r.status} /></TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">{fmtDateTime(r.at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {sent.length === 0 && (
            <div className="border-t border-dashed border-border px-5 py-12 text-center text-sm text-muted-foreground" data-testid="reports-sent-empty">
              No reports emailed yet. Open a job and tap "Email to client".
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
